import React, { Component, Fragment } from 'react'
import { Form, Formik } from 'formik'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, media, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H4, SmallText, Text, TinyText } from 'app/common/typography'
import { CheckOrRadio, Error, Input, Password } from 'app/common/forms'
import { boolean, object, string } from 'yup'
import { Link } from 'app/common/link'
import { Checkbox, EmptyCircle, RadioChecked } from 'app/assets/icons'
import {
    authType,
    featureCodes,
    formatDate,
    isAllowedToSeeStudentFeature,
    isCorrectBirthdate,
    regex
} from 'shared/utils'
import type { LoginForm, RestaurantShortDTO, SignUpForm } from 'shared/types'
import { SocialConnect } from 'app/common/social-connect'
import { ModalTitle } from 'app/common/modals/title'
import { Recaptcha } from 'app/common/recaptcha'
import { Loader } from 'app/common/loader'
import { FavoriteRestaurant } from 'app/kingdom/profile/favorite-restaurant'
import { FeatureToggle } from 'app/common/feature-toggle'
import { marketSelect } from '../../utils'

const { grey, lightGrey, red, bbqBrown } = colors

type Props = {
    signup: SignUpForm => void,
    resendMail: (queen: string) => void,
    reset: () => void,
    success: boolean,
    login: string,
    sendFormError?: any,
    intl: IntlShape,
    history: Object,
    navigate: (to: string) => void,
    userData: LoginForm,
    backUrl?: string,
    fromUrl?: string,
    redirectToAfterLogin?: string,
    studentAgeLimit?: number,
    goToRedirectAndReset: (url: string) => string,
    kingdomMinAge?: number
}

type State = {
    showCaptcha: boolean
}

class SignupForm extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    componentDidMount() {
        this.props.fetchSignupPageData()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    back = (e: Event) => {
        const { navigate, reset, intl, backUrl } = this.props
        reset()
        navigate(
            intl.formatMessage({
                id: backUrl ? backUrl : 'routes.signin'
            })
        )
    }

    formSubscription = () => {
        const { userData, intl, kingdomMinAge } = this.props
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            email: string()
                .required(userData && userData.login ? false : mandatory)
                .matches(regex.EMAIL, {
                    message: 'form.error.regex.email',
                    excludeEmptyString: true
                }),
            password: string()
                .required(userData ? false : mandatory)
                .matches(regex.PASSWORD, {
                    message: 'form.error.regex.password',
                    excludeEmptyString: true
                }),
            birthdate: string()
                .required(mandatory)
                .test(
                    'isValidDate',
                    intl.formatMessage(
                        {
                            id: 'signup.form.birthdate.error'
                        },
                        { minAge: kingdomMinAge }
                    ),
                    value => {
                        if (value) {
                            if (value.match(regex.DATE)) {
                                const date = value.split('/')
                                const dateFormatted = `${date[2]}-${date[1]}-${date[0]}`

                                return isCorrectBirthdate(
                                    dateFormatted,
                                    kingdomMinAge
                                )
                            } else if (value.match(regex.DATE_ISO)) {
                                return isCorrectBirthdate(value, kingdomMinAge)
                            }
                        }
                        return false
                    }
                ),
            favoriteRestaurant: object().required(mandatory),
            optin: boolean().required(mandatory),
            studentDeclaration: boolean().required(),
            agreements: boolean()
                .required()
                .oneOf([true], 'signup.form.agreements.error')
        })
    }

    render() {
        const {
            success,
            login,
            resendMail,
            signup,
            sendFormError,
            fromUrl,
            userData,
            intl,
            studentAgeLimit
        } = this.props
        const { showCaptcha } = this.state
        const initialValues: any = {
            email: '',
            password: '',
            birthdate: '',
            favoriteRestaurant: undefined,
            optin: undefined,
            agreements: false,
            studentDeclaration: false
        }

        return (
            <Fragment>
                {showCaptcha && (
                    <Recaptcha
                        action="WebSignup"
                        verifyCallback={this.verifyCallback}
                    />
                )}
                {success ? (
                    <Fragment>
                        <FormattedMessage
                            id="signup.success.message.mail"
                            values={{ mail: login }}>
                            {(text: string) => (
                                <Paragraph>
                                    <Text>{text}</Text>
                                </Paragraph>
                            )}
                        </FormattedMessage>
                        <Paragraph>
                            <FormattedMessage id="signup.success.message.resend.start">
                                {(text: string) => <Text>{text}</Text>}
                            </FormattedMessage>
                            <FormattedMessage id="signup.success.message.resend.link">
                                {(text: string) => (
                                    <RetryLink
                                        onClick={async () => {
                                            try {
                                                this.setState({
                                                    showCaptcha: true
                                                })
                                                const recaptchaToken = await this
                                                    .recaptchaPromise
                                                resendMail(recaptchaToken)
                                            } finally {
                                                this.setState({
                                                    showCaptcha: false
                                                })
                                                this.initRecaptchaPromise()
                                            }
                                        }}>
                                        {text}
                                    </RetryLink>
                                )}
                            </FormattedMessage>
                            <FormattedMessage id="signup.success.message.resend.end">
                                {(text: string) => <Text>{text}</Text>}
                            </FormattedMessage>
                        </Paragraph>
                        <FormattedMessage id="signup.success.message.kingdom">
                            {(text: string) => <Text>{text}</Text>}
                        </FormattedMessage>
                        <ButtonContainer>
                            <FormattedMessage id="signup.success.button.label">
                                {(text: string) => (
                                    <StyledButtonMediumSuccess url="/">
                                        {text}
                                    </StyledButtonMediumSuccess>
                                )}
                            </FormattedMessage>
                        </ButtonContainer>
                    </Fragment>
                ) : (
                    <Content social={userData !== undefined}>
                        {userData && (
                            <ModalTitle
                                center
                                closable={false}
                                titleNode={
                                    <FormattedMessage id="signup.form.title">
                                        {(text: string) => (
                                            <StyledH4>{text}</StyledH4>
                                        )}
                                    </FormattedMessage>
                                }
                            />
                        )}
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            onSubmit={async (values, actions) => {
                                let birthdate = formatDate(values.birthdate)
                                const {
                                    email,
                                    password,
                                    favoriteRestaurant,
                                    optin,
                                    studentDeclaration
                                } = values
                                let data = {
                                    birthdate,
                                    login: email.trim(),
                                    password,
                                    loginType: authType.CLASSIC,
                                    uid: '',
                                    favRestaurantFrNumber:
                                        favoriteRestaurant &&
                                        favoriteRestaurant.id,
                                    optin,
                                    student: studentDeclaration
                                }
                                if (userData) {
                                    const { login } = userData
                                    data = {
                                        ...data,
                                        ...userData,
                                        login: login || data.login
                                    }
                                }
                                try {
                                    this.setState({ showCaptcha: true })
                                    const recaptchaToken = await this
                                        .recaptchaPromise
                                    await signup({
                                        ...data,
                                        recaptchaToken
                                    })
                                    if (
                                        this.props.authenticated &&
                                        this.props.redirectToAfterLogin
                                    ) {
                                        this.props.goToRedirectAndReset(
                                            this.props.redirectToAfterLogin
                                        )
                                    }
                                } finally {
                                    this.setState({ showCaptcha: false })
                                    this.initRecaptchaPromise()
                                    actions.setSubmitting(false)
                                }
                            }}
                            validationSchema={this.formSubscription}>
                            {({
                                values,
                                errors,
                                touched,
                                isSubmitting,
                                setFieldValue
                            }) => (
                                <Form>
                                    <FeatureToggle
                                        feature={featureCodes.AUTHENT}>
                                        {!userData && (
                                            <SocialConnect
                                                redirectUrl={
                                                    fromUrl
                                                        ? fromUrl
                                                        : intl.formatMessage({
                                                              id:
                                                                  'routes.kingdom.dashboard'
                                                          })
                                                }
                                            />
                                        )}
                                    </FeatureToggle>
                                    <FeatureToggle
                                        feature={featureCodes.AUTHENT_CLASSIC}>
                                        {(!userData || !userData.login) && (
                                            <Fragment>
                                                <Input
                                                    name="email"
                                                    type="email"
                                                    labelKey="signup.form.email.title"
                                                    placeholderKey="form.email.placeholder"
                                                    className={
                                                        errors.email &&
                                                        touched.email
                                                            ? 'error'
                                                            : ''
                                                    }
                                                />
                                                {errors.email &&
                                                    touched.email && (
                                                        <Error
                                                            errorKey={
                                                                errors.email
                                                            }
                                                        />
                                                    )}
                                            </Fragment>
                                        )}

                                        {!userData && (
                                            <Fragment>
                                                <Password
                                                    name="password"
                                                    type="password"
                                                    labelKey="signup.form.password.title"
                                                    placeholderKey="signup.form.password.placeholder"
                                                    className={
                                                        errors.password &&
                                                        touched.password
                                                            ? 'error'
                                                            : ''
                                                    }
                                                />
                                                {errors.password &&
                                                    touched.password && (
                                                        <Error
                                                            errorKey={
                                                                errors.password
                                                            }
                                                        />
                                                    )}
                                            </Fragment>
                                        )}
                                        <Input
                                            name="birthdate"
                                            type="date"
                                            labelKey="form.birthdate.title"
                                            placeholderKey="form.date.placeholder"
                                            className={`date ${
                                                errors.birthdate &&
                                                touched.birthdate
                                                    ? 'error'
                                                    : ''
                                            }`}
                                        />
                                        {errors.birthdate &&
                                            touched.birthdate && (
                                                <Error
                                                    errorKey={errors.birthdate}
                                                />
                                            )}

                                        <RestaurantContainer>
                                            <FavoriteRestaurant
                                                title={
                                                    <FavoriteRestaurantTitle
                                                        color={grey}>
                                                        <FormattedMessage id="form.favorite.restaurant.title" />
                                                    </FavoriteRestaurantTitle>
                                                }
                                                favRestaurant={
                                                    values.favoriteRestaurant
                                                }
                                                onClick={(
                                                    selectedRestaurant: RestaurantShortDTO
                                                ) => {
                                                    setFieldValue(
                                                        'favoriteRestaurant',
                                                        selectedRestaurant
                                                    )
                                                }}
                                            />
                                            {errors.favoriteRestaurant &&
                                                touched.favoriteRestaurant && (
                                                    <Error
                                                        errorKey={
                                                            errors.favoriteRestaurant
                                                        }
                                                    />
                                                )}
                                        </RestaurantContainer>
                                        <Optin>
                                            <SmallText>
                                                <FormattedMessage
                                                    id={marketSelect({
                                                        france:
                                                            'signup.form.optin',
                                                        reunion:
                                                            'signup.form.optin.reunion'
                                                    })}
                                                />
                                            </SmallText>
                                            <CheckMention>
                                                <CheckOrRadio
                                                    name="optin"
                                                    type="radio"
                                                    checked={values.optin}
                                                    label={intl
                                                        .formatMessage({
                                                            id: 'yes'
                                                        })
                                                        .toString()
                                                        .toUpperCase()}>
                                                    <div
                                                        onClick={() =>
                                                            setFieldValue(
                                                                'optin',
                                                                true
                                                            )
                                                        }>
                                                        {values.optin ===
                                                        true ? (
                                                            <RadioChecked />
                                                        ) : (
                                                            <EmptyCircle />
                                                        )}
                                                    </div>
                                                </CheckOrRadio>
                                                <CheckOrRadio
                                                    name="optin"
                                                    type="radio"
                                                    checked={
                                                        values.optin === false
                                                    }
                                                    label={intl
                                                        .formatMessage({
                                                            id: 'no'
                                                        })
                                                        .toString()
                                                        .toUpperCase()}>
                                                    <RadioOptin
                                                        onClick={() =>
                                                            setFieldValue(
                                                                'optin',
                                                                false
                                                            )
                                                        }>
                                                        {values.optin ===
                                                        false ? (
                                                            <RadioChecked />
                                                        ) : (
                                                            <EmptyCircle />
                                                        )}
                                                    </RadioOptin>
                                                </CheckOrRadio>
                                            </CheckMention>
                                        </Optin>
                                        {errors.optin && touched.optin && (
                                            <Error errorKey={errors.optin} />
                                        )}

                                        <FeatureToggle
                                            feature={
                                                featureCodes.NATIONAL_STUDENT_BLAZON_REGISTRATION
                                            }>
                                            {isAllowedToSeeStudentFeature(
                                                values.birthdate,
                                                studentAgeLimit,
                                                'YYYY-MM-DD'
                                            ) && (
                                                <>
                                                    <CheckMention
                                                        onClick={() =>
                                                            setFieldValue(
                                                                'studentDeclaration',
                                                                !values.studentDeclaration
                                                            )
                                                        }>
                                                        <CheckOrRadio
                                                            name="studentDeclaration"
                                                            type="checkbox"
                                                            labelKey="signup.form.student-declaration"
                                                            checked={
                                                                values.studentDeclaration
                                                            }
                                                            className={`agreements
                                    ${
                                        errors.studentDeclaration &&
                                        touched.studentDeclaration
                                            ? 'error'
                                            : ''
                                    }`}>
                                                            <Checkbox
                                                                checked={
                                                                    values.studentDeclaration
                                                                }
                                                                color={
                                                                    errors.studentDeclaration &&
                                                                    touched.studentDeclaration &&
                                                                    red
                                                                }
                                                            />
                                                        </CheckOrRadio>
                                                    </CheckMention>
                                                </>
                                            )}
                                        </FeatureToggle>
                                        <CheckMention
                                            onClick={() =>
                                                setFieldValue(
                                                    'agreements',
                                                    !values.agreements
                                                )
                                            }>
                                            <CheckOrRadio
                                                name="agreements"
                                                type="checkbox"
                                                labelKey="signup.form.agreements"
                                                linkKey="signup.form.agreements.link"
                                                labelKeyEnd="signup.form.agreements.end"
                                                url={`${intl.formatMessage({
                                                    id: 'routes.custom'
                                                })}/${intl.formatMessage({
                                                    id: 'routes.cgv.fidelity'
                                                })}`}
                                                openLinkInNewTab
                                                checked={values.agreements}
                                                className={`agreements
                                    ${
                                        errors.agreements && touched.agreements
                                            ? 'error'
                                            : ''
                                    }`}>
                                                <Checkbox
                                                    checked={values.agreements}
                                                    color={
                                                        errors.agreements &&
                                                        touched.agreements &&
                                                        red
                                                    }
                                                />
                                            </CheckOrRadio>
                                        </CheckMention>
                                        <Loader loading={isSubmitting} />
                                        {errors.agreements &&
                                            touched.agreements && (
                                                <Error
                                                    errorKey={errors.agreements}
                                                />
                                            )}
                                        {userData ? (
                                            <ButtonContainer marginBottom>
                                                <StyledButtonMedium
                                                    onClick={e => this.back(e)}
                                                    background={lightGrey}
                                                    color={grey}
                                                    disabled={isSubmitting}>
                                                    <FormattedMessage id="form.button.cancel" />
                                                </StyledButtonMedium>
                                                <StyledButtonMedium
                                                    type="submit"
                                                    background={bbqBrown}
                                                    disabled={isSubmitting}>
                                                    <FormattedMessage id="signup.form.button.send" />
                                                </StyledButtonMedium>
                                            </ButtonContainer>
                                        ) : (
                                            <ButtonContainer marginBottom>
                                                <StyledButtonMedium
                                                    type="submit"
                                                    background={bbqBrown}
                                                    disabled={isSubmitting}>
                                                    <FormattedMessage id="signup.form.button.send" />
                                                </StyledButtonMedium>
                                            </ButtonContainer>
                                        )}
                                        {sendFormError && (
                                            <Mentions>
                                                <FormattedMessage
                                                    id={sendFormError}>
                                                    {(text: string) => (
                                                        <Text color={red}>
                                                            {text}
                                                        </Text>
                                                    )}
                                                </FormattedMessage>
                                            </Mentions>
                                        )}
                                        <Mentions>
                                            <FormattedMessage id="form.mandatory.information">
                                                {(text: string) => (
                                                    <TinyText color={grey}>
                                                        {text}
                                                    </TinyText>
                                                )}
                                            </FormattedMessage>
                                        </Mentions>
                                    </FeatureToggle>
                                </Form>
                            )}
                        </Formik>
                    </Content>
                )}
            </Fragment>
        )
    }
}

const StyledH4 = styled(H4)`
    text-align: center;
`

const Content = styled.div`
    padding: ${props => (props.social ? '0 20px' : '0')};
    ${media.medium`
        padding: ${props => (props.social ? '40px' : '0')};
    `};
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const CheckMention = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
    margin: 20px 0 10px;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px 0 ${props => (props.marginBottom ? '30px' : 0)};
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    margin-right: 10px;
    border: none;
`

const RadioOptin = styled.div`
    margin-left: 40px;
`

const Paragraph = styled.p``

const RetryLink = styled(Text)`
    cursor: pointer;
    text-decoration: underline;
`

const StyledButtonMediumSuccess = styled(Link)`
    ${style.buttonMedium};
    align-self: center;
    border: none;
    background: ${bbqBrown};
`

const RestaurantContainer = styled.div`
    margin: 15px 0;
    display: flex;
    flex-direction: column;
`

const FavoriteRestaurantTitle = styled(SmallText)`
    margin-bottom: 10px;
`

const Optin = styled.div`
    background-color: rgba(247, 168, 0, 0.2);
    border-radius: 10px;
    padding: 10px;
    margin-top: 25px;
`

export const SignupFormComponent = injectIntl(SignupForm)
